import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Briefcase, Loader2, Trash2, Save, AlertTriangle } from "lucide-react";
import { ProjectSidebar } from "@/components/agency/ProjectSidebar";
import { NewProjectDialog } from "@/components/agency/NewProjectDialog";
import { BriefFields, toForm, fromForm } from "@/components/agency/BriefFields";
import { QueryPanel } from "@/components/agency/QueryPanel";
import { LeadsReview } from "@/components/agency/LeadsReview";
import { PROJECT_STATUS, errMsg, isBusy } from "@/components/agency/utils";
import { CostCard } from "@/components/agency/CostCard";
import { WorkflowGuide } from "@/components/agency/WorkflowGuide";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const StatusBar = ({ project }) => {
  const busy = isBusy(project.status);
  const r = project.last_run;
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600" data-testid="agency-status-bar">
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${project.status === "error" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-slate-200 bg-slate-50"}`} data-testid="agency-project-status">
        {busy && <Loader2 className="w-3 h-3 animate-spin" />}
        {project.status === "error" && <AlertTriangle className="w-3 h-3" />}
        {PROJECT_STATUS[project.status] || project.status}
      </span>
      {project.progress?.message && <span data-testid="agency-progress-message">{String(project.progress.message)}</span>}
      {r?.warning && project.status !== "error" && (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-800" data-testid="agency-last-run-warning">
          <AlertTriangle className="w-3 h-3" /> Explee: {String(r.warning)}
        </span>
      )}
      {r && (
        <span className="text-slate-500" data-testid="agency-last-run-stats">
          Τελευταία εκτέλεση: {r.candidates} υποψήφιοι · +{r.added} νέα · {r.duplicates} διπλά · {r.excluded_title} εκτός ρόλου · {r.domain_cap} όριο εταιρείας
        </span>
      )}
    </div>
  );
};

export default function AgencyWorkbench() {
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [brief, setBrief] = useState(toForm());
  const busy = project && isBusy(project.status);

  const loadList = useCallback(() => api.get("/agency/projects").then((r) => setProjects(r.data)), []);
  const open = useCallback((id) => api.get(`/agency/projects/${id}`)
    .then((r) => { setProject(r.data); setBrief(toForm(r.data)); })
    .catch((e) => toast.error(errMsg(e))), []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(async () => {
      let data;
      try {
        ({ data } = await api.get(`/agency/projects/${project.id}`));
      } catch (e) {
        return;
      }
      setProject(data);
      if (!isBusy(data.status)) {
        loadList();
        const msg = String(data.progress?.message || "");
        if (data.status === "error") toast.error(msg || "Σφάλμα");
        else if (data.last_run?.warning) toast.warning(`${msg} · Explee: ${data.last_run.warning}`);
        else toast.success(msg || "Ολοκληρώθηκε");
      }
    }, 2500);
    return () => clearInterval(t);
  }, [busy, project?.id, loadList]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBrief = async () => {
    try {
      const { data } = await api.patch(`/agency/projects/${project.id}`, fromForm(brief));
      setProject(data);
      loadList();
      toast.success("Το brief αποθηκεύτηκε");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    if (!window.confirm(`Διαγραφή project «${project.client_name}»;`)) return;
    await api.delete(`/agency/projects/${project.id}`);
    setProject(null);
    loadList();
  };

  return (
    <div className="space-y-6 pb-24 lg:pb-0" data-testid="agency-workbench-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#0052CC]">Internal · Super Admin</div>
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Briefcase className="w-8 h-8 text-[#0052CC]" /> AI Lead Agency Workbench
        </h1>
        <p className="text-slate-600 mt-1 text-sm">Brief → Queries → Αναζήτηση → Έλεγχος → Excel για τους πελάτες σας.</p>
      </div>
      <div className="flex flex-col lg:flex-row gap-6">
        <ProjectSidebar projects={projects} activeId={project?.id} onSelect={open} onNew={() => setNewOpen(true)} />
        <section className="flex-1 min-w-0 bg-white border border-slate-200 rounded-lg p-5 space-y-4">
          {!project ? (
            <div className="py-16 text-center text-slate-500" data-testid="agency-empty-state">Επιλέξτε ή δημιουργήστε ένα project πελάτη.</div>
          ) : (
            <ErrorBoundary key={project.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900" data-testid="agency-project-title">{project.client_name}</h2>
                  <StatusBar project={project} />
                </div>
                <Button variant="ghost" size="sm" className="text-rose-600" onClick={remove} disabled={busy} data-testid="agency-delete-project-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Διαγραφή
                </Button>
              </div>
              <WorkflowGuide project={project} />
              <div className="flex flex-col xl:flex-row gap-5">
              <div className="flex-1 min-w-0">
              <Tabs key={project.id} defaultValue={project.leads?.length ? "leads" : "brief"}>
                <TabsList className="flex-wrap h-auto justify-start">
                  <TabsTrigger value="brief" data-testid="agency-tab-brief">1. Brief</TabsTrigger>
                  <TabsTrigger value="queries" data-testid="agency-tab-queries">2. Queries & Αναζήτηση</TabsTrigger>
                  <TabsTrigger value="leads" data-testid="agency-tab-leads">3. Έλεγχος & Excel ({project.leads?.length || 0})</TabsTrigger>
                </TabsList>
                <TabsContent value="brief" className="pt-4 space-y-4">
                  <BriefFields value={brief} onChange={setBrief} />
                  <Button onClick={saveBrief} disabled={busy} data-testid="agency-save-brief-btn"><Save className="w-4 h-4 mr-1" /> Αποθήκευση brief</Button>
                </TabsContent>
                <TabsContent value="queries" className="pt-4">
                  <QueryPanel project={project} busy={busy} onUpdated={setProject} />
                </TabsContent>
                <TabsContent value="leads" className="pt-4">
                  <LeadsReview project={project} busy={busy}
                    onLeadsChange={(leads) => setProject({ ...project, leads })}
                    onJobStarted={(status) => setProject({ ...project, status, progress: { message: PROJECT_STATUS[status] } })} />
                </TabsContent>
              </Tabs>
              </div>
              <div className="xl:w-72 shrink-0"><CostCard cost={project.cost} /></div>
              </div>
            </ErrorBoundary>
          )}
        </section>
      </div>
      <NewProjectDialog open={newOpen} onOpenChange={setNewOpen}
        onCreated={(p) => { setNewOpen(false); loadList(); open(p.id); }} />
    </div>
  );
}
